import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { ComponentDefinition, IValueViewModel, GroupNameDto, IStatistics, TagDateValue, SeriesDefinition, Identifier, StatsData, RefreshClickData, DigitalSetSimpleDictionary, ActualDataType, Dictionary } from '../../../models';
import { IExportToCsvService, ITagPageService, IFormatNumberHelper, ISeriesService, IDateRangeService, IStoreService, IDigitalSetHelperService } from '../../../services';
import { infoTextColor } from '../../../common/constants';
import { IWidget } from '../../../widgets';
import { ChartConfigurationOption } from '../../../wtFramework';
import { StoreElement, Store } from '../../../stateManagement';
import { refreshClick } from '../../../common/events';

export interface ICalculatedStateParams extends angular.ui.IStateParamsService {
    identifierGuid: string;
    beginDate?: string;
    endDate?: string;
}

class CalculatedGeneralStateController implements angular.IComponentController, angular.IOnChanges {
    isLoaded: boolean;
    isLoading: boolean;
    loadingMessage: string;
    identifierGuid: string;
    identifier: Identifier;
    statistics: IWidget;
    target: boolean;
    menu: any[];
    inputValuesQuality: { configuration: { type: string; guid: string } };
    initialRange: { beginDate: any, endDate: any; };
    currentInterval: any;
    currentBeginDate: string;
    currentEndDate: string;
    identifierGroup: GroupNameDto;
    stats: IStatistics;
    data: TagDateValue[];
    statsData: StatsData;
    options: ChartConfigurationOption;
    series: SeriesDefinition<TagDateValue>[];
    identifierVm: IValueViewModel;
    viewModels: Dictionary<IValueViewModel>;
    digitalSets: DigitalSetSimpleDictionary;

    private _interval: { beginDate: string; endDate: string; force: boolean; };
    private _garbages: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$stateParams',
        'exportToCsvService',
        'tagPageService',
        'formatNumberHelper',
        'seriesService',
        'dateRangeService',
        'storeService',
        'digitalSetHelperService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$stateParams: ICalculatedStateParams,
        private _exportToCsvService: IExportToCsvService,
        private _tagPageService: ITagPageService,
        private _formatNumberHelper: IFormatNumberHelper,
        private _seriesService: ISeriesService,
        private _dateRangeService: IDateRangeService,
        private _storeService: IStoreService,
        private _digitalSetHelperService: IDigitalSetHelperService
    ) {
    }

    $onInit() {
        this.data = [];
        this._garbages = [];
        this.identifierGuid = this._$stateParams.identifierGuid;
        this.inputValuesQuality = { configuration: { type: 'identifier', guid: this.identifierGuid } };
        this.initializeMenu();

        // Events handling
        this._garbages.push(this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
            this._interval = {
                beginDate: data.beginDate,
                endDate: data.endDate,
                force: data.force
            };
            if (this.series && this.series.length) {
                this.refresh(data.beginDate, data.endDate, data.force);
            }
        }));
        this.isLoading = true;
        this.isLoaded = false;
        this._garbages.push(
            this._storeService.register((store) => this.storeUpdate(store), [
                StoreElement.DigitalSets,
                StoreElement.ValueViewModels
            ])
        );
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (onChangesObj['identifierVm'] && onChangesObj['identifierVm'].currentValue) {
            if (!this.options) {
                this.initializeOptions();
            }
            this.loadSeries(this.identifierVm);
        }
    }

    $onDestroy() {
        if (this._garbages) {
            this._garbages.forEach(garbage => garbage());
        }
    }

    initializeOptions() {
        const options = new ChartConfigurationOption();
        options.isTitleVisible = false;
        options.isLegendVisible = false;
        options.isHorizontalGridlinesVisible = true;
        options.panes = [{
            height: 1,
            isXaxisVisible: true,
            isYaxisVisible: true,
            isHorizontalGridLinesVisible: true,
            horizontalLines: [],
            isTitleLeftAligned: false,
            minHeight: null
        }];
        if (this.identifierVm) {
            const horizontalLines = this._tagPageService.horizontalLines(this.identifierVm);
            options.panes[0].horizontalLines = horizontalLines;
        }
        this.options = options;
    }

    initializeMenu() {
        this.menu = [{
            model: this.target,
            label: 'Target',
            update: (value: any) => this.target = value
        }];
    }

    private storeUpdate(store: Store): void {
        this.digitalSets = store.digitalSetDictionary.dictionary;
        this.viewModels = store.valueViewModels.dictionary;
        this.isLoaded = true;
        this.isLoading = false;
        if (!!this.identifierVm) {
            this.loadSeries(this.identifierVm);
        }
    }

    loadSeries(vm: IValueViewModel) {
        const isDigital = vm.actualDataType === ActualDataType.Digital;
        const crosshairFormat = isDigital
            ? d => d.comment
            : d => this._formatNumberHelper.getFormatter(2).format(d.value);
        this.series = [
            {
                name: vm.name,
                identifier: vm.guid + 'average',
                value: d => d.value,
                selector: d => d.guid + d.valueType,
                color: infoTextColor,
                symbol: 'circle',
                isLineVisible: false,
                isSymbolVisible: true,
                serieState: 0,
                crosshairFormat: crosshairFormat
            },
            this._seriesService.createMissingSerie()
        ];
        const horizontalLines = this._tagPageService.horizontalLines(this.identifierVm);
        this.options.panes[0].horizontalLines = horizontalLines;
        if (this._interval) {
            this.refresh(this._interval.beginDate, this._interval.endDate, this._interval.force);
        }
    }

    exportCsv() {
        const self = this;
        const dto = [{
            guid: this.identifierVm.guid,
            type: 'identifier',
            value: 'average'
        }];
        this.loadingMessage = 'Exporting data to csv';
        this.isLoading = true;
        this._exportToCsvService.getCsv(dto, 'Trend')
            .finally(() => {
                self.isLoading = false;
            });
    }

    //TODO exact same method as in Tag page
    refresh(beginDate?: string, endDate?: string, force: boolean = false) {
        if (this.isLoaded) {
            this.loadingMessage = 'Loading data';
            this.isLoading = true;
            const guid = this.identifierGuid;
            if (this._interval) {
                if (!beginDate) {
                    beginDate = this._interval.beginDate;
                }
                if (!endDate) {
                    endDate = this._interval.endDate;
                }
                if (force !== false && this._interval.force === true) {
                    force = this._interval.force;
                }
            } else {
                if (!beginDate) {
                    beginDate = this._dateRangeService.beginDate() as string;
                }
                if (!endDate) {
                    endDate = this._dateRangeService.endDate() as string;
                }
            }

            if (this.options) {
                this.options.display.xMin = moment(beginDate).toDate();
                this.options.display.xMax = moment(endDate).toDate();
            }
            const valueType = 'average';
            this.isLoading = true;
            this._tagPageService
                .readData(guid, beginDate, endDate, valueType, this.digitalSets, this.viewModels, force)
                .then(identifierData => {
                    this.data = identifierData.preparedData;
                    this.statsData = identifierData.statsData;
                })
                .catch(reason => {
                    console.error(reason);
                })
                .finally(() => this.isLoading = false);
        }
    }
}

export const wtCalculatedGeneralState: ComponentDefinition = {
    componentId: 'wtCalculatedGeneralState',
    controller: CalculatedGeneralStateController,
    template: require('./calculatedGeneralState.component.html'),
    controllerAs: 'vm',
    bindings: {
        identifierVm: '<'
    }
};
